
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { History, MessageSquare, Edit } from "lucide-react";

const ChatHistory = () => {
  const { user } = useAuth();
  const { data: chatHistory, isLoading } = useQuery({
    queryKey: ["chat-history"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("chat_history")
        .select(`
          id,
          last_message_at,
          created_at,
          posits (
            id,
            title
          )
        `)
        .eq("user_id", user?.id)
        .order("last_message_at", { ascending: false });

      if (error) throw error;

      // For each chat history, determine the session type by checking associated messages
      const enhancedHistory = await Promise.all(
        (data || []).map(async (chat) => {
          // Check if this chat history has any update messages
          const { data: updateMessages } = await supabase
            .from('chat_messages')
            .select('source')
            .eq('chat_history_id', chat.id)
            .eq('source', 'posit_update')
            .limit(1);

          const isUpdateSession = updateMessages && updateMessages.length > 0;
          
          return {
            ...chat,
            sessionType: isUpdateSession ? 'update' : 'chat'
          };
        })
      );

      return enhancedHistory;
    },
    enabled: !!user?.id,
  });

  // Clean up posit title to remove "Chat with" prefix if present
  const cleanTitle = (title: string) => {
    return title.replace(/^(chat with\s*)/i, '').trim();
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-8">
        <div className="animate-pulse">
          <div className="h-8 w-48 bg-gray-200 rounded mb-6"></div>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8">
      <div className="flex items-center gap-2 mb-6">
        <History className="w-6 h-6" />
        <h1 className="text-2xl font-bold">Chat History</h1>
      </div>
      
      <div className="space-y-4">
        {chatHistory?.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500 mb-4">No chat history found</p>
            <Button asChild>
              <Link to="/dashboard/posits">View Your Posits</Link>
            </Button>
          </div>
        ) : (
          chatHistory?.map((chat) => (
            <Link
              key={chat.id}
              to={`/chat-details/${chat.id}`}
              className="block p-4 rounded-lg border border-border hover:border-primary transition-colors"
            >
              <div className="flex items-center gap-3 mb-2">
                {chat.sessionType === 'update' ? (
                  <Edit className="w-4 h-4 text-blue-600" />
                ) : (
                  <MessageSquare className="w-4 h-4 text-green-600" />
                )}
                <h2 className="font-medium">{cleanTitle(chat.posits.title)}</h2>
              </div>
              <p className="text-sm text-gray-500 ml-7">
                {chat.sessionType === 'update' ? 'Updated' : 'Last message'}: {format(new Date(chat.last_message_at), "PPp")}
              </p>
            </Link>
          ))
        )}
      </div>
    </div>
  );
};

export default ChatHistory;
